import { useCallback, useEffect, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import {
  controlCenterService,
  ControlFilters,
} from "@/services/control-center.service";
import { useAdminResource } from "@/hooks/useAdminResource";
import { Button } from "./Button";
import { Input } from "./Input";
import { Select } from "./Select";

export const billingLabel = (value: string) =>
  value === "byok" ? "Own key (BYOK)" : "Slightor managed";
export const nullableUsd = (value?: number | null) =>
  value == null
    ? "Not recorded"
    : new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 4,
      }).format(value);
export const latency = (value?: number | null) =>
  value == null
    ? "Not recorded"
    : `${(value / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })}s`;

export function AiReportFilters({
  value,
  onChange,
  withStatus = false,
}: {
  value: ControlFilters;
  onChange: (value: ControlFilters) => void;
  withStatus?: boolean;
}) {
  const [search, setSearch] = useState(""),
    [query, setQuery] = useState("");
  const [model, setModel] = useState(value.model ?? ""),
    [feature, setFeature] = useState(value.feature ?? "");
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search), 300);
    return () => window.clearTimeout(timer);
  }, [search]);
  useEffect(() => {
    setModel(value.model ?? "");
    setFeature(value.feature ?? "");
  }, [value.model, value.feature]);
  const {
    data: companies,
    error,
    loading,
  } = useAdminResource(
    useCallback(() => controlCenterService.usageCompanies(query), [query]),
  );
  const change = (patch: Partial<ControlFilters>) =>
    onChange({ ...value, ...patch, page: 1 });
  return (
    <section className="admin-panel p-3" aria-label="Usage filters">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label="Reporting period"
          className="w-auto"
          value={value.period ?? String(value.days ?? 30)}
          onChange={(event) => {
            const selected = event.target.value;
            change(
              selected === "today" || selected === "month"
                ? { period: selected, days: undefined }
                : { period: undefined, days: Number(selected) },
            );
          }}
        >
          <option value="today">Today · UTC</option>
          <option value="month">This month · UTC</option>
          {[7, 30, 90].map((days) => (
            <option key={days} value={days}>
              Last {days} days
            </option>
          ))}
        </Select>
        <Select
          aria-label="Provider"
          className="w-auto"
          value={value.provider ?? ""}
          onChange={(event) =>
            change({ provider: event.target.value || undefined })
          }
        >
          <option value="">All providers</option>
          {["openai", "claude", "gemini", "qwen", "mistral", "elevenlabs"].map(
            (id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ),
          )}
        </Select>
        <Select
          aria-label="Billing mode"
          className="w-auto"
          value={value.billingMode ?? ""}
          onChange={(event) =>
            change({ billingMode: event.target.value || undefined })
          }
        >
          <option value="">All billing modes</option>
          <option value="slightor_managed">Slightor managed</option>
          <option value="byok">Own key (BYOK)</option>
        </Select>
        {withStatus && (
          <Select
            aria-label="Request status"
            className="w-auto"
            value={value.status ?? ""}
            onChange={(event) =>
              change({ status: event.target.value || undefined })
            }
          >
            <option value="">All statuses</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
            <option value="pending">Pending / reserved</option>
          </Select>
        )}
        <details className="basis-full">
          <summary className="flex cursor-pointer list-none items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-text-secondary hover:bg-surface-alt">
            <SlidersHorizontal size={14} />
            Company & more
            {(value.companyUid || value.model || value.feature) && (
              <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            )}
          </summary>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              change({
                model: model.trim() || undefined,
                feature: feature.trim() || undefined,
              });
            }}
            className="mt-2 grid items-end gap-3 rounded-xl border border-border bg-surface-alt p-3 sm:grid-cols-2 lg:grid-cols-3"
          >
            <label className="block space-y-1 text-xs text-text-secondary">
              <span>Find a company</span>
              <Input
                maxLength={100}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search by name"
                iconLeft={<Search size={14} />}
              />
            </label>
            <Select
              aria-label="Company"
              value={value.companyUid ?? ""}
              onChange={(event) =>
                change({ companyUid: event.target.value || undefined })
              }
            >
              <option value="">All companies</option>
              {value.companyUid &&
                !companies?.some(
                  (row) => row.companyUid === value.companyUid,
                ) && (
                  <option value={value.companyUid}>{value.companyUid}</option>
                )}
              {companies?.map((row) => (
                <option key={row.companyUid} value={row.companyUid}>
                  {row.name}
                </option>
              ))}
            </Select>
            <p className="text-[11px] text-text-muted">
              {error
                ? "Company search unavailable. Try again."
                : loading
                  ? "Finding companies…"
                  : "First 25 matches. Search to narrow the list."}
            </p>
            <label className="block space-y-1 text-xs text-text-secondary">
              <span>Model</span>
              <Input
                maxLength={128}
                value={model}
                onChange={(event) => setModel(event.target.value)}
                placeholder="Exact model ID"
              />
            </label>
            <label className="block space-y-1 text-xs text-text-secondary">
              <span>Feature</span>
              <Input
                maxLength={128}
                value={feature}
                onChange={(event) => setFeature(event.target.value)}
                placeholder="Exact feature name"
              />
            </label>
            <div className="flex justify-between gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearch("");
                  change({
                    companyUid: undefined,
                    model: undefined,
                    feature: undefined,
                  });
                }}
              >
                Clear
              </Button>
              <Button type="submit" size="sm">
                Apply
              </Button>
            </div>
          </form>
        </details>
      </div>
      {(value.companyUid || value.model || value.feature) && (
        <p className="mt-2 break-words text-xs text-text-muted">
          {[
            value.companyUid &&
              `Company: ${companies?.find((row) => row.companyUid === value.companyUid)?.name ?? value.companyUid}`,
            value.model && `Model: ${value.model}`,
            value.feature && `Feature: ${value.feature}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      )}
    </section>
  );
}
